import { ClassData } from "../../../common/dtos/classes.type";
import { submissionService } from "../../../common/services/submission.service";
import { trackingService } from "../../../common/services/tracking.service";
import { getIP } from "../../../common/utils/ip.util";
import { attachCampaignDataObject, getCookie, writeCookie } from "../../campaign";

interface FormData {
  name: string;
  email: string;
  phone: string;
  smsConsent: boolean;
}

export class EventsModalService {
  private static getFormData(uniqueStyle: string): FormData {
    return {
      name: ($(`${uniqueStyle} #name`).val() as string) || "",
      email: ($(`${uniqueStyle} #email`).val() as string) || "",
      phone: ($(`${uniqueStyle} #phone`).val() as string) || "",
      smsConsent: !!$(`${uniqueStyle} #sms-consent`).prop("checked"),
    };
  }

  private static validateFormData(data: FormData): boolean {
    const isNameValid = this.validateName(data.name);
    const isEmailValid = this.validateEmail(data.email);
    const isPhoneValid = this.validatePhone(data.phone);

    if (!isNameValid) {
      this.showError(
        ".input-wrapper #name",
        "Please enter your full name (at least 2 characters)."
      );
    } else {
      this.hideError(".input-wrapper #name");
    }

    if (!isEmailValid) {
      this.showError(
        ".input-wrapper #email",
        "Please enter a valid email address."
      );
    } else {
      this.hideError(".input-wrapper #email");
    }

    if (!isPhoneValid) {
      this.showError(
        ".input-wrapper #phone",
        "Please enter a valid phone number (at least 10 digits)."
      );
    } else {
      this.hideError(".input-wrapper #phone");
    }

    return isNameValid && isEmailValid && isPhoneValid;
  }

  /**
   * Public sync wrapper around getFormData + validateFormData so the
   * modal click handler can decide whether to open the destination tab
   * before kicking off the async submit. Returns true when the form is
   * valid and ready to submit.
   */
  public static preValidate(uniqueStyle: string = ""): boolean {
    return this.validateFormData(this.getFormData(uniqueStyle));
  }

  private static validateName(name: string): boolean {
    return name.trim().length >= 2;
  }

  private static validatePhone(phone: string): boolean {
    // Accept common formatting characters but require at least 10 digits
    // (US-style minimum). Strip non-digits before counting.
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 15) return false;
    const phoneRegex = /^[+]?[0-9\s().-]{7,20}$/;
    return phoneRegex.test(phone);
  }

  private static showError(selector: string, message: string): void {
    const $input = $(selector);
    let $error = $input.next(".error-message");
    if (!$error.length) {
      $error = $("<div>").addClass("error-message");
      $input.after($error);
      $($error).show();
    }
    $error.text(message);
  }

  private static hideError(selector: string): void {
    const $input = $(selector);
    $input.next(".error-message").remove();
  }

  private static validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private static async submitCRMData(
    formData: FormData,
    classData: ClassData,
    registrationLink: string
  ) {
    try {
      const nonce = ryb_connect_ajax?.nonce;
      if (!nonce) {
        throw new Error("Security nonce not found");
      }

      const currentUrl = new URL(window.location.href);
      const stepParam = currentUrl.searchParams.get("step");
      let updatedStep = stepParam ? parseInt(stepParam, 10) + 1 : 1;

      currentUrl.searchParams.set("step", updatedStep.toString());

      const requestBody = {
        body: attachCampaignDataObject({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          dnd: false,
          dndSettings: {
            Call: {
              status: "inactive",
              message: "",
            },
            Email: {
              status: "inactive",
              message: "",
            },
            SMS: {
              // "inactive" = NOT on do-not-disturb (= consented to receive).
              // "active" = DND on (= no consent / opted out). TCPA requires
              // explicit opt-in, so default to active when consent isn't checked.
              status: formData.smsConsent ? "inactive" : "active",
              message: formData.smsConsent ? "Consented via enroll modal" : "No SMS consent given",
            },
            WhatsApp: {
              status: "inactive",
              message: "",
            },
            GMB: {
              status: "inactive",
              message: "",
            },
            FB: {
              status: "inactive",
              message: "",
            },
          },
          provider_id: 1,
          source: "website",
          customFields: [
            {
              key: "selected_class_id",
              field_value: classData.id,
            },
            {
              key: "selected_class_url",
              field_value: registrationLink,
            },
            {
              key: "last_funnel_url",
              field_value: window.location.href,
            },
          ],
        })
      };

      const response = await $.ajax({
        url: `https://${window.location.host}/wp-json/ryb/v1/ghl?timestamp=${Date.now()}`,
        method: "POST",
        contentType: "application/json",
        data: JSON.stringify(requestBody),
      });

      const sentLastAttributionSource = requestBody?.body?.lastAttributionSource;
      const contact = response?.data?.contact;
      
      if (response.success && sentLastAttributionSource && (!contact?.lastAttributionSource || Object.keys(contact.lastAttributionSource || {}).length === 0)) {
        trackingService.captureException(
          new Error(
            `${window.location.host} | lastAttributionSource was sent but not returned in contact response`
          ),
          {
            devMessage: 'lastAttributionSource was sent but not returned in contact response',
            response: response,
          }
        );
      }

      if (!response.success) {
        trackingService.captureException(
          new Error(
            `${window.location.host} | Submit CRM data to GoHighLevel - GHL API responded with error in modal`
          ),
          {
            devMessage:
              "Check plugin logs, either the token was not refreshed or their API is down",
            response: response,
          }
        );
      }

          if(response.success === true && response?.data?.contact?.id) {
      const contactId = response?.data?.contact?.id;
      localStorage.setItem('ghlContactId', contactId);
      const tagsResponse = await $.ajax({
        url: `https://${window.location.host}/wp-json/ryb/v1/ghl?timestamp=${Date.now()}`,
        method: "POST",
        contentType: "application/json",
        data: JSON.stringify({
          request_url: `https://services.leadconnectorhq.com/contacts/${contactId}/tags`,
          include_location_id: false,
          body: {
            tags: typeof ryb_connect_ghl_tags !== "undefined"
          ? (ryb_connect_ghl_tags[
              document.querySelector("#class-finder.template-new-camps") ? "new-camps"
              : document.querySelector("#class-finder.template-camps") ? "camps"
              : document.querySelector("#class-finder.template-trials") ? "trial"
              : "default"
            ] || ["webFunnel"])
          : (document.querySelector("#class-finder.template-trials") ? ["trial"] : ["webFunnel"]),
          }
        })
      })

      if (tagsResponse?.success === false) {
        trackingService.captureException(
          new Error(
            `${window.location.host} | Submit CRM data (TAGS) to GoHighLevel - GHL API responded with error in wizard step child`
          ),
          {
            devMessage:
              "Check plugin logs, the tag request failed, so the users will not have tags",
            response: response,
          }
        );
      }
    }

      return { response, requestBody };
    } catch (error) {
      trackingService.captureException(
        new Error(
          `${window.location.host} | Submit CRM data to GoHighLevel - GHL API responded with error in modal`
        ),
        {
          devMessage:
            "Check plugin logs, either the token was not refreshed or their API is down",
          error: error,
        }
      );

      return null;
    }
  }

  private static async submitGAData(formData: FormData) {
    const classData = getCookie("classData");
    const eventData = attachCampaignDataObject({
      site_name: window.location.host,
      event: "lead created", // for enrollment - enroll, for contact form - lead created
      name: formData.name,
      email: formData?.email ? formData.email : "No email provided",
      timestamp: Date.now(),
      ip: await getIP(),
      page_title: document.querySelector("title")?.innerText,
      class_data: classData ? JSON.parse(decodeURIComponent(escape(atob(classData)))) : {},
    });
    fetch("https://a.cloud.dive.games/ryb/events", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(eventData),
    })
      .then((data) => {
        console.log("Success:", data);
      })
      .catch((error) => {
        trackingService.captureException(
          new Error(
            `${window.location.host} | a.cloud.dive.games API returned error on request in modal popup`
          ),
          {
            devMessage: "a.cloud.dive.games returned error on request",
            requestObject: eventData,
            error: error,
          }
        );
        console.error("Error:", error);
      });
  }

  public static async handleSubmit(
    cardData: ClassData,
    registrationLink: string,
    uniqueStyle: string = ""
  ): Promise<{ success: boolean }> {
    const data = this.getFormData(uniqueStyle);
    if (!this.validateFormData(data)) {
      return { success: false };
    }

    // Persist user data to the rybUser cookie *before* the destination URL
    // is built. Jackrabbit URL pre-fill (buildJackrabbitUrl) reads from this
    // cookie, so writing it now means the new tab opens with the user's
    // freshly typed name/email/phone instead of stale cookie data.
    try {
      const userData: Record<string, any> = {
        name: data.name,
        email: data.email,
        phone: data.phone,
        ghlId: localStorage.getItem("ghlContactId") || null,
      };
      writeCookie(
        "rybUser",
        btoa(unescape(encodeURIComponent(JSON.stringify(userData)))),
        365
      );
    } catch (cookieError) {
      console.error("Failed to write rybUser cookie:", cookieError);
    }

    trackingService.pushEvent("ryb_event_form_submitted", {
      event_name: cardData.name || "",
      class_name: cardData.name || "",
    });

    const nameParts = data.name.trim().split(/\s+/);
    await submissionService.createSubmission({
      first_name: nameParts[0] || "",
      last_name: nameParts.slice(1).join(" ") || "",
      email: data.email,
      phone: data.phone,
      sms_consent: data.smsConsent ? 1 : 0,
      selected_class_name: cardData.name || undefined,
      selected_class_id: cardData.id ? String(cardData.id) : undefined,
      funnel_type: "events",
      funnel_last_step: "event_form",
    });

    const result = await this.submitCRMData(data, cardData, registrationLink);
    const response = result?.response;
    const requestBody = result?.requestBody;
    if (response?.success && response?.data?.contact?.id) {
      trackingService.pushEvent("ryb_event_lead_created", {
        ghl_contact_id: response.data.contact.id,
        event_name: cardData.name || "",
      });
      trackingService.pushEvent("generate_lead", {
        currency: "USD",
        value: 0,
      });
      await submissionService.updateSubmission({
        ghl_status: "success",
        ghl_contact_id: response.data.contact.id,
        ghl_response: response?.data || response,
        ghl_request: requestBody?.body || requestBody,
        funnel_last_step: "event_lead_created",
      });
    } else {
      await submissionService.updateSubmission({ ghl_status: "failed", ghl_response: response?.data || response, ghl_request: requestBody?.body || requestBody });
    }

    await this.submitGAData(data);
    return { success: true };
  }
}
